
import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './AuthContext';
import { toast } from 'sonner';

export interface Notification {
  id: string;
  title: string;
  message: string;
  type: 'info' | 'success' | 'warning' | 'error';
  read: boolean;
  createdAt: Date;
  actionUrl?: string;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  addNotification: (notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => void;
  markAsRead: (id: string) => void;
  markAllAsRead: () => void;
  clearNotification: (id: string) => void;
  clearAllNotifications: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const { user } = useAuth();

  const addNotification = (notification: Omit<Notification, 'id' | 'read' | 'createdAt'>) => {
    const newNotification: Notification = {
      ...notification,
      id: crypto.randomUUID(),
      read: false,
      createdAt: new Date(),
    };
    setNotifications(prev => [newNotification, ...prev]);
    
    // Show toast for new notifications
    toast(notification.title, {
      description: notification.message,
      duration: 4000,
    });
  };

  const markAsRead = (id: string) => {
    setNotifications(prev =>
      prev.map(notification =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
  };

  const markAllAsRead = () => {
    setNotifications(prev =>
      prev.map(notification => ({ ...notification, read: true }))
    );
  };

  const clearNotification = (id: string) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id));
  };

  const clearAllNotifications = () => {
    setNotifications([]);
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Enhanced real-time subscriptions
  useEffect(() => {
    if (!user) return;

    console.log('Setting up real-time notifications for user:', user.id);

    // Create a single channel for all user notifications
    const channel = supabase
      .channel(`user-notifications-${user.id}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'generated_content',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const content = payload.new;
          console.log('Content generation event:', content);
          
          if (content.status === 'completed') {
            addNotification({
              title: 'Content Ready! 🎉',
              message: `Your ${content.type} "${content.title || 'content'}" has been generated successfully`,
              type: 'success',
              actionUrl: '/dashboard?tab=library'
            });
          } else if (content.status === 'failed') {
            addNotification({
              title: 'Generation Failed ❌',
              message: `Failed to generate ${content.type}. Please try again or contact support.`,
              type: 'error'
            });
          } else if (content.status === 'processing') {
            addNotification({
              title: 'Processing Started ⚙️',
              message: `Your ${content.type} generation is now in progress`,
              type: 'info'
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'profiles',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const oldProfile = payload.old;
          const newProfile = payload.new;
          console.log('Profile update event:', { old: oldProfile, new: newProfile });
          
          // Credits deduction notification
          if (oldProfile.credits_used !== newProfile.credits_used) {
            const creditsUsed = newProfile.credits_used - oldProfile.credits_used;
            if (creditsUsed > 0) {
              addNotification({
                title: 'Credits Updated 💳',
                message: `${creditsUsed} credit${creditsUsed > 1 ? 's' : ''} used. ${newProfile.credits_limit - newProfile.credits_used} remaining`,
                type: 'info'
              });
            }
          }

          // Plan upgrade notification
          if (oldProfile.plan !== newProfile.plan) {
            addNotification({
              title: 'Plan Upgraded! 🚀',
              message: `Welcome to ${newProfile.plan.charAt(0).toUpperCase() + newProfile.plan.slice(1)} plan! Enjoy enhanced features.`,
              type: 'success',
              actionUrl: '/billing'
            });
          }
        }
      )
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'subscribers',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          const subscription = payload.new;
          console.log('Subscription update event:', subscription);
          
          if (subscription.status === 'active') {
            addNotification({
              title: 'Subscription Activated! 🎊',
              message: `Your ${subscription.subscription_tier} subscription is now active`,
              type: 'success',
              actionUrl: '/billing'
            });
          } else if (subscription.status === 'cancelled') {
            addNotification({
              title: 'Subscription Cancelled',
              message: 'Your subscription has been cancelled. You can reactivate it anytime.',
              type: 'warning',
              actionUrl: '/billing'
            });
          }
        }
      )
      .subscribe((status) => {
        console.log('Notification subscription status:', status);
      });

    return () => {
      console.log('Cleaning up notification subscriptions');
      supabase.removeChannel(channel);
    };
  }, [user]);

  // Welcome notification for new users
  useEffect(() => {
    if (user && notifications.length === 0) {
      setTimeout(() => {
        addNotification({
          title: 'Welcome to Genova AI! 👋',
          message: 'Start creating amazing content with our AI-powered tools',
          type: 'info',
          actionUrl: '/dashboard'
        });
      }, 2000);
    }
  }, [user]);

  return (
    <NotificationContext.Provider
      value={{
        notifications,
        unreadCount,
        addNotification,
        markAsRead,
        markAllAsRead,
        clearNotification,
        clearAllNotifications,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
};
